import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "virtual-tree-root" }
const _hoisted_2 = {
  key: 0,
  class: "selection-panel"
}
const _hoisted_3 = { class: "selection-panel__left" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "tree-back__label" }
const _hoisted_6 = {
  key: 2,
  class: "virtual-tree__no-results"
}
const _hoisted_7 = {
  key: 3,
  class: "virtual-tree-body"
}
const _hoisted_8 = ["data-index"]

import { ref, reactive, computed, watch, toRaw, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useDrop } from 'vue3-dnd';
import { useVirtualizer } from '@tanstack/vue-virtual';
import BaseListElementLite from './BaseListElementLite.vue';
import ConfirmActionPopup from '@/components/common/basic/confirm/ConfirmActionPopup.vue';
import { BaseListItem } from './interfaces/ListTypes';
import { BaseItemTypes, DragItemTypes } from '@/components/dnd/ItemTypes';
import { i18n } from '@/i18n';
import { generalStore } from '@/store/general';
import { searchVersionRef } from './searchState';
import { insertAnimActive } from './insertAnimState';
import {
  CHILD_ARRAYS,
  canDropToRoot,
  isFolderDropForbidden,
  isImmediateParentByIdentity,
  allowChildToChildDropAction as _allowChildToChildDropAction,
  allowChildToRootDropAction as _allowChildToRootDropAction,
  allowRootToRootDropAction as _allowRootToRootDropAction,
  allowRootToChildDropAction as _allowRootToChildDropAction,
  allowHighlightOnHover as _allowHighlightOnHover,
} from './dndRules';

interface FlatItem {
  item: BaseListItem;
  depth: number;
  isRoot: boolean;
  levelSiblings: BaseListItem[];
  /** Локальный индекс элемента среди siblings того же уровня (а не в плоском виртуальном списке). */
  siblingIndex: number;
  parentId: number | undefined;
  parentType: BaseItemTypes | undefined;
  hasChildren: boolean;
  effectiveExpanded: boolean;
  inReportContext: boolean;
  key: string;
}

interface FavoriteTreeState {
  visibleKeys: Set<string>;
  autoExpandKeys: Set<string>;
}

type ModeName = 'search' | 'favorite';

interface ModeSession {
  active: boolean;
  snapshot: Map<string, boolean> | null;
  dirty: Set<string>;
}

/**
 * Независимые сессии для search/favorite.
 * Каждый режим хранит собственный snapshot и dirty, чтобы вести себя как отдельный режим.
 */
const MEASURE_BATCH = 8;

interface DragItem { id: string; callbackParams: any; type: string }


export default /*@__PURE__*/_defineComponent({
  __name: 'VirtualAnalyticsList',
  props: {
    inputItems: {},
    searchString: {},
    favoriteMode: { type: Boolean },
    pendingCreateFolderByParent: {},
    pendingMoveByKey: {},
    betweenRootDropViaIndicator: { type: Boolean },
    backEnabled: { type: Boolean }
  },
  emits: ["cardSelected", "cardDeleted", "cardFavorite", "cardRenamed", "cardCloned", "changeParent", "addChildSelection", "addChildProjection", "addChildList", "addChildChart", "addChildFolder", "addToReport", "deleteSelected", "breakAfter", "changePosition", "cardMoved", "back"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const betweenRootDropViaIndicator = computed(() => !!props.betweenRootDropViaIndicator);

const emit = __emit;

const scrollEl = ref<HTMLElement | null>(null);
const deleteBtnRef = ref<HTMLElement | null>(null);
const genstore = generalStore();

// --- Мультивыбор элементов ---

const selectionMode = ref(false);

/**
 * Карта выбранных элементов. Ключ — составная строка Identity (elType:Id:FolderId:SrcType:DestType),
 * значение — полный объект callbackParams-формата, совместимый с canDropElement/canDropToRoot из dndRules.ts.
 * При будущем мультидраге: for (item of selectedItems.values()) canDropElement(item, hoverEl).
 */
const selectedItems: Map<string, any> = reactive(new Map());

const lastClickPos = ref({ x: 0, y: 0 });
function trackClickPos(e: MouseEvent) { lastClickPos.value = { x: e.clientX, y: e.clientY }; }
onMounted(() => document.addEventListener('mousedown', trackClickPos, true));
onBeforeUnmount(() => document.removeEventListener('mousedown', trackClickPos, true));

// Одноразовая подсветка элемента при переходе по ссылке гасится первым кликом мыши где угодно.
// Слушатель навешиваем ТОЛЬКО когда подсветка реально активна и с отложкой (setTimeout 0),
// чтобы не поймать тот же mousedown-жест, которым открыли элемент (иначе подсветка гаснет мгновенно).
let clearHighlightArmed = false;
function onClearHighlightMousedown() {
  genstore.clearLinkHighlight();
  disarmClearHighlight();
}
function armClearHighlight() {
  if (clearHighlightArmed) return;
  clearHighlightArmed = true;
  setTimeout(() => {
    if (!genstore.linkHighlight) { clearHighlightArmed = false; return; }
    document.addEventListener('mousedown', onClearHighlightMousedown, true);
  }, 0);
}
function disarmClearHighlight() {
  document.removeEventListener('mousedown', onClearHighlightMousedown, true);
  clearHighlightArmed = false;
}
watch(
  () => genstore.linkHighlight,
  (hl) => { if (hl) armClearHighlight(); else disarmClearHighlight(); },
  { immediate: true },
);
onBeforeUnmount(() => disarmClearHighlight());

const confirmPopup = reactive({
  show: false,
  position: { x: 0, y: 0 },
  title: '',
  question: '',
  confirmLabel: '',
  cancelLabel: '',
  onConfirm: () => {},
  onCancel: () => {},
});

/** Универсальный ключ полной Identity: для папок учитывается FolderIdentity, для остальных — полный AnalyticsElementIdentity. */
function makeSelectionKey(p: any): string {
  const elType = p.elType ?? '';
  const id = p.id ?? p.Id ?? '';
  const folderId = p.folderId ?? p.FolderId ?? '';
  const src = p.sourceDataType ?? p.SrcType ?? '';
  const dest = p.destinationDataType ?? p.DestType ?? '';
  // Для дочерних элементов в отчётах одна и та же сущность (Id+elType+SrcType+DestType)
  // может встречаться в разных отчётах; чтобы различать конкретные ссылки, добавляем ElementId.
  const innerElementId = p.innerElementId ?? p.ElementId ?? '';
  return `${elType}:${id}:${folderId}:${src}:${dest}:${innerElementId}`;
}

/** Конвертирует BaseListItem (PascalCase) в camelCase-формат callbackParams для единообразного хранения в selectedItems. */
function toCallbackParams(item: BaseListItem): any {
  const fi = flatItems.value.find(f => makeSelectionKey(f.item) === makeSelectionKey(item));
  return {
    id: item.Id,
    folderId: item.FolderId,
    parent_id: fi?.parentId,
    parent_type: fi?.parentType,
    isRoot: fi?.isRoot ?? false,
    elType: item.elType,
    sourceDataType: item.SrcType,
    destinationDataType: item.DestType,
    innerElementId: (item as any).ElementId,
  };
}

/**
 * Собирает ВСЕ дочерние элементы (рекурсивно по CHILD_ARRAYS) из узла дерева.
 * Возвращает массив самих BaseListItem потомков (любой глубины).
 */
function getAllDescendants(item: BaseListItem): BaseListItem[] {
  const result: BaseListItem[] = [];
  for (const key of CHILD_ARRAYS) {
    const arr = (item as any)[key] as BaseListItem[] | undefined;
    if (!arr) continue;
    for (const child of arr) {
      result.push(child);
      result.push(...getAllDescendants(child));
    }
  }
  return result;
}

/**
 * Возвращает ключи всех потомков элемента (для быстрого удаления из selectedItems).
 */
function getAllDescendantKeys(item: BaseListItem): string[] {
  return getAllDescendants(item).map(d => makeSelectionKey(d));
}

/**
 * Собирает прямых детей элемента (один уровень, все CHILD_ARRAYS).
 */
function getDirectChildren(item: BaseListItem): BaseListItem[] {
  const result: BaseListItem[] = [];
  for (const key of CHILD_ARRAYS) {
    const arr = (item as any)[key] as BaseListItem[] | undefined;
    if (arr) result.push(...arr);
  }
  return result;
}

/**
 * Ищет BaseListItem в flatItems по ключу (callbackParams или BaseListItem).
 * Нужно, чтобы из callbackParams (camelCase) получить узел дерева с дочерними массивами.
 */
function findTreeItemByKey(key: string): BaseListItem | null {
  const fi = flatItems.value.find(f => makeSelectionKey(f.item) === key);
  return fi?.item ?? null;
}

/**
 * Ищет FlatItem в flatItems по ключу.
 */
function findFlatItemByKey(key: string): FlatItem | null {
  return flatItems.value.find(f => makeSelectionKey(f.item) === key) ?? null;
}

/**
 * Проверяет, является ли элемент потомком (на любом уровне) другого элемента.
 * Работает по дереву (CHILD_ARRAYS), а не по flatItems, чтобы покрыть свёрнутые ветки.
 */
function isDescendantOf(descendant: BaseListItem, ancestor: BaseListItem): boolean {
  const descKey = makeSelectionKey(descendant);
  return getAllDescendantKeys(ancestor).includes(descKey);
}

/**
 * Находит реально выбранного предка (из selectedItems) для данного элемента.
 * Идёт вверх по дереву через flatItems.parentId/parentType.
 * Возвращает FlatItem предка или null, если предок не выбран.
 */
function findSelectedAncestor(itemKey: string): FlatItem | null {
  const fi = findFlatItemByKey(itemKey);
  if (!fi || fi.parentId == null) return null;

  // Поднимаемся по дереву вверх через parentId/parentType.
  // Для папок сравниваем по FolderId, для остальных — по Id (без упрощений).
  let current = fi;
  while (current.parentId != null) {
    const parentFi = flatItems.value.find(f => {
      if (f.item.elType !== current.parentType) return false;
      if (current.parentType === BaseItemTypes.FOLDER) {
        return (f.item as any).FolderId === current.parentId;
      }
      return (f.item as any).Id === current.parentId;
    });
    if (!parentFi) break;
    const parentKey = makeSelectionKey(parentFi.item);
    if (selectedItems.has(parentKey)) return parentFi;
    current = parentFi;
  }
  return null;
}

/**
 * «Взрыв» выделения предка: когда пользователь снимает галку с унаследованного элемента,
 * предок убирается из selectedItems, а все ветки дерева между предком и снятым элементом
 * «расщепляются» — каждый сиблинг на пути становится реальным выделением.
 *
 * Пример: A (real) -> B1, B2, B3; B2 -> C1, C2, C3. Пользователь снимает C2.
 * Результат: A убирается. B1, B3 добавляются. C1, C3 добавляются. C2 — нет.
 */
function explodeSelection(ancestorItem: BaseListItem, uncheckedItem: BaseListItem) {
  // Строим путь от предка к снятому элементу через дерево
  const path: BaseListItem[] = [];

  function buildPath(current: BaseListItem, target: BaseListItem): boolean {
    path.push(current);
    if (makeSelectionKey(current) === makeSelectionKey(target)) return true;
    for (const key of CHILD_ARRAYS) {
      const arr = (current as any)[key] as BaseListItem[] | undefined;
      if (!arr) continue;
      for (const child of arr) {
        if (buildPath(child, target)) return true;
      }
    }
    path.pop();
    return false;
  }

  buildPath(ancestorItem, uncheckedItem);

  // На каждом уровне пути добавляем сиблингов (не входящих в путь) как реальные выделения
  for (let i = 0; i < path.length - 1; i++) {
    const parent = path[i];
    const nextOnPath = path[i + 1];
    const nextOnPathKey = makeSelectionKey(nextOnPath);
    const children = getDirectChildren(parent);
    for (const child of children) {
      if (makeSelectionKey(child) !== nextOnPathKey) {
        selectedItems.set(makeSelectionKey(child), toCallbackParams(child));
      }
    }
  }
}

/**
 * Проверяет, выбран ли элемент унаследованно (не сам, а через предка).
 */
function isInheritedSelected(item: BaseListItem): boolean {
  const key = makeSelectionKey(item);
  if (selectedItems.has(key)) return false; // реально выбран, не унаследован
  return findSelectedAncestor(key) !== null;
}

/**
 * Проверяет, выбран ли элемент эффективно (реально ИЛИ унаследованно).
 */
function isEffectivelySelected(item: BaseListItem): boolean {
  const key = makeSelectionKey(item);
  return selectedItems.has(key) || findSelectedAncestor(key) !== null;
}

function enterSelectionMode(initialParams?: any) {
  // Массовое выделение недоступно в дереве отчётов: ни сам REPORT, ни его потомки
  // не должны участвовать в multi-select.
  if (initialParams?.elType === BaseItemTypes.REPORT) return;
  selectionMode.value = true;
  if (initialParams) {
    handleCheck(initialParams);
  }
}

function exitSelectionMode() {
  selectionMode.value = false;
  selectedItems.clear();
}

function resetSelection() {
  selectedItems.clear();
}

/**
 * Обработка включения галки: добавляем элемент, убираем всех потомков
 * (родитель покрывает их).
 */
function handleCheck(params: any) {
  const key = makeSelectionKey(params);
  const treeItem = findTreeItemByKey(key);

  selectedItems.set(key, params);

  if (treeItem) {
    for (const dk of getAllDescendantKeys(treeItem)) {
      selectedItems.delete(dk);
    }
  }
}

/**
 * Обработка снятия галки: если элемент реально выбран — просто убираем.
 * Если унаследован — «взрываем» выделение предка, расщепляя на сиблингов.
 */
function handleUncheck(params: any) {
  const key = makeSelectionKey(params);

  if (selectedItems.has(key)) {
    const treeItem = findTreeItemByKey(key);
    const children = treeItem ? getDirectChildren(treeItem) : [];

    // Упрощённый сценарий по требованию: при снятии родителя ведём себя как "Да" в старом подтверждении.
    selectedItems.delete(key);
    if (children.length === 0) {
      return;
    }
  } else {
    const ancestorFi = findSelectedAncestor(key);
    if (!ancestorFi) return;
    const ancestorKey = makeSelectionKey(ancestorFi.item);
    selectedItems.delete(ancestorKey);

    const treeItem = findTreeItemByKey(key);
    if (treeItem) {
      explodeSelection(ancestorFi.item, treeItem);
    }
  }
}

function onToggleItemSelection(params: any) {
  const key = makeSelectionKey(params);
  const effectivelySelected = selectedItems.has(key) || findSelectedAncestor(key) !== null;

  if (effectivelySelected) {
    handleUncheck(params);
  } else {
    handleCheck(params);
  }
}

function deleteSelected() {
  const rect = deleteBtnRef.value?.getBoundingClientRect();
  const pos = rect ? { x: rect.left, y: rect.bottom + 4 } : { x: 0, y: 0 };
  emit('deleteSelected', Array.from(selectedItems.values()), pos);
}

function getSelectedIdentities(): any[] {
  return Array.from(selectedItems.values());
}

/**
 * Возвращает эффективный набор переносимых элементов:
 * - реально выбранные (зелёные),
 * - их дочерние (синие информационные),
 * без дублей по полной Identity.
 */
function getEffectiveSelectedIdentities(): any[] {
  const result: any[] = [];
  const used = new Set<string>();

  for (const [, val] of selectedItems) {
    const key = makeSelectionKey(val);
    if (!used.has(key)) {
      used.add(key);
      result.push(val);
    }

    const treeItem = findTreeItemByKey(key);
    if (!treeItem) continue;
    const descendants = getAllDescendants(treeItem);
    for (const child of descendants) {
      const childParams = toCallbackParams(child);
      const childKey = makeSelectionKey(childParams);
      if (used.has(childKey)) continue;
      used.add(childKey);
      result.push(childParams);
    }
  }

  return result;
}

/**
 * Общее количество эффективно выбранных элементов (реальные + все их потомки).
 * Используется в счётчике drag-превью: показываем полное число затрагиваемых элементов.
 */
const effectiveSelectedCount = computed(() => {
  let count = 0;
  for (const [, val] of selectedItems) {
    count += 1; // сам элемент
    const treeItem = findTreeItemByKey(makeSelectionKey(val));
    if (treeItem) count += getAllDescendants(treeItem).length;
  }
  return count;
});

/**
 * Определяет DnD-тип элемента (CARD_ROOT / CARD_CHILD) по его callbackParams.
 * Логика аналогична dragType в BaseListElementLite:
 * корневые выборки/проекции/списки → CARD_ROOT, папки и дочерние → CARD_CHILD.
 */
function dragTypeForItem(p: any): string {
  return p.isRoot && p.elType !== BaseItemTypes.FOLDER
    ? DragItemTypes.CARD_ROOT
    : DragItemTypes.CARD_CHILD;
}

/**
 * Обёртка для мульти-проверки DnD-правил: если активен режим выделения и есть выбранные элементы,
 * проверяем правило для КАЖДОГО реально выбранного элемента (AND). Дроп разрешён, только если
 * все элементы могут быть перемещены в цель. Для одиночного перетаскивания вызывается исходная функция.
 */
function multiCheckDndRule(
  singleFn: (dragParams: any, hoverParams: any, tree: BaseListItem[]) => boolean,
  dragParams: any,
  hoverParams: any,
): boolean {
  if (selectionMode.value && selectedItems.size > 0) {
    for (const sel of selectedItems.values()) {
      const dp = { type: dragTypeForItem(sel), callbackParams: sel };
      if (!singleFn(dp, hoverParams, props.inputItems)) return false;
    }
    return true;
  }
  return singleFn(dragParams, hoverParams, props.inputItems);
}

// --- DnD: обёртки над чистыми функциями из dndRules.ts ---
// При мульти-выделении проверяем правила для каждого выбранного элемента (AND).

const intoForbiddenFn = (dragParams: any, hoverParams: any) => {
  if (isFolderDropForbidden(dragParams, hoverParams, props.inputItems)) return true;
  if (selectionMode.value && selectedItems.size > 0) {
    const hoverEl = hoverParams?.callbackParams ?? hoverParams;
    for (const sel of selectedItems.values()) {
      if (isImmediateParentByIdentity(props.inputItems, sel, hoverEl)) return true;
      if (isFolderDropForbidden(
        { callbackParams: sel, type: dragTypeForItem(sel) },
        hoverParams,
        props.inputItems,
      )) return true;
    }
  }
  return false;
};

const allowChildToChildDropAction = (dragParams: any, hoverParams: any) =>
  multiCheckDndRule(_allowChildToChildDropAction, dragParams, hoverParams);

const allowChildToRootDropAction = (dragParams: any, hoverParams: any) =>
  multiCheckDndRule(_allowChildToRootDropAction, dragParams, hoverParams);

const allowRootToRootDropAction = (dragParams: any, hoverParams: any) =>
  multiCheckDndRule(_allowRootToRootDropAction, dragParams, hoverParams);

const allowRootToChildDropAction = (dragParams: any, hoverParams: any) =>
  multiCheckDndRule(_allowRootToChildDropAction, dragParams, hoverParams);

const allowHighlightOnHover = (dragParams: any, hoverParams: any) =>
  multiCheckDndRule(_allowHighlightOnHover, dragParams, hoverParams);

// --- Flatten tree ---

const modeSessions = reactive<Record<ModeName, ModeSession>>({
  search: { active: false, snapshot: null, dirty: new Set<string>() },
  favorite: { active: false, snapshot: null, dirty: new Set<string>() },
});

/**
 * Текущий режим (последний включённый). Только в него пишем ручные dirty-изменения.
 */
const currentMode = ref<ModeName | null>(null);

function walkTree(items: BaseListItem[], onNode: (item: BaseListItem) => void) {
  for (const item of items) {
    onNode(item);
    for (const arrKey of CHILD_ARRAYS) {
      const arr = (item as any)[arrKey] as BaseListItem[] | undefined;
      if (!arr || arr.length === 0) continue;
      walkTree(arr, onNode);
    }
  }
}

function captureExpandSnapshot(): Map<string, boolean> {
  const snapshot = new Map<string, boolean>();
  walkTree(props.inputItems, (item) => {
    snapshot.set(makeSelectionKey(item), !!item.isExpanded);
  });
  return snapshot;
}

function startModeSession(mode: ModeName) {
  const session = modeSessions[mode];
  session.active = true;
  session.snapshot = captureExpandSnapshot();
  session.dirty = new Set();
  currentMode.value = mode;
}

function restoreModeSession(mode: ModeName) {
  const session = modeSessions[mode];
  const snapshot = session.snapshot;
  if (!snapshot) return;
  const dirty = session.dirty;

  // Merge-логика: ручные изменения пользователя (dirty) имеют приоритет над snapshot.
  walkTree(props.inputItems, (item) => {
    const key = makeSelectionKey(item);
    if (dirty.has(key)) return;
    if (!snapshot.has(key)) return;
    item.isExpanded = !!snapshot.get(key);
  });

  session.snapshot = null;
  session.dirty = new Set();
  session.active = false;
}

function applyModeAutologic(mode: ModeName) {
  // Для search отдельного "применения" не нужно: _searchAutoExpand учитывается в flattenTree.
  if (mode === 'favorite') {
    applyFavoriteAutoExpandOnce(props.inputItems, favoriteTreeState.value.autoExpandKeys);
  }
}

function otherMode(mode: ModeName): ModeName {
  return mode === 'search' ? 'favorite' : 'search';
}

function stopModeSession(mode: ModeName) {
  const session = modeSessions[mode];
  if (!session.active) return;

  // Если выключается не текущий режим, дерево не трогаем.
  // Это сохраняет ожидаемое поведение: search(on)->favorite(on)->search(off) без визуальных изменений.
  if (currentMode.value !== mode) {
    session.active = false;
    session.snapshot = null;
    session.dirty = new Set();
    return;
  }

  restoreModeSession(mode);
  const fallback = otherMode(mode);
  if (modeSessions[fallback].active) {
    currentMode.value = fallback;
    // Оставшийся активный режим применяем как "единственный".
    applyModeAutologic(fallback);
    return;
  }
  currentMode.value = null;
}

function markExpandDirtyByUser(item: BaseListItem) {
  if (!currentMode.value) return;
  const session = modeSessions[currentMode.value];
  if (!session.active) return;
  session.dirty.add(makeSelectionKey(item));
}

const isSearchModeActive = computed(() => !!props.searchString);

watch(
  () => props.searchString,
  (nextValue, prevValue) => {
    if (nextValue === prevValue) return;
    if (scrollEl.value) {
      scrollEl.value.scrollTop = 0;
    }
  },
);

watch(isSearchModeActive, (enabled, wasEnabled) => {
  if (enabled && !wasEnabled) {
    startModeSession('search');
    return;
  }
  if (!enabled && wasEnabled) {
    stopModeSession('search');
    const selected = toRaw(genstore.selectedItem as any);
    if (selected && selected.elType !== undefined) {
      // Используем уже существующую deep-link цепочку:
      // programmatic select (с раскрытием пути) + trigger scroll к selection.
      const selectedIdentity = {
        Id: selected.Id,
        FolderId: selected.FolderId,
        SrcType: selected.SrcType,
        DestType: selected.DestType,
        elType: selected.elType,
        ElementId: selected.ElementId,
      } as BaseListItem;
      genstore.setSelectedAfter(selectedIdentity, true);
      genstore.applyElementAction(selectedIdentity, 'select', true, false, 'programmatic');
    }
  }
}, { immediate: true });

function applyFavoriteAutoExpandOnce(items: BaseListItem[], autoExpandKeys: Set<string>) {
  for (const item of items) {
    const key = makeSelectionKey(item);
    if (autoExpandKeys.has(key)) {
      item.isExpanded = true;
    }
    for (const arrKey of CHILD_ARRAYS) {
      const arr = (item as any)[arrKey] as BaseListItem[] | undefined;
      if (!arr || arr.length === 0) continue;
      applyFavoriteAutoExpandOnce(arr, autoExpandKeys);
    }
  }
}

const favoriteTreeState = computed<FavoriteTreeState>(() => {
  const visibleKeys = new Set<string>();
  const autoExpandKeys = new Set<string>();

  if (!props.favoriteMode) {
    return { visibleKeys, autoExpandKeys };
  }

  const walk = (items: BaseListItem[], hasFavoriteAncestor: boolean): boolean => {
    let branchHasFavorite = false;

    for (const item of items) {
      const key = makeSelectionKey(item);
      const isFavorite = !!(item as any).IsFavorite;

      let hasFavoriteChild = false;
      for (const arrKey of CHILD_ARRAYS) {
        const arr = (item as any)[arrKey] as BaseListItem[] | undefined;
        if (!arr || arr.length === 0) continue;
        if (walk(arr, hasFavoriteAncestor || isFavorite)) {
          hasFavoriteChild = true;
        }
      }

      const subtreeHasFavorite = isFavorite || hasFavoriteChild;
      const visibleByFavorite = hasFavoriteAncestor || subtreeHasFavorite;
      if (visibleByFavorite) {
        visibleKeys.add(key);
      }

      if (hasFavoriteChild) {
        autoExpandKeys.add(key);
      }

      if (subtreeHasFavorite) {
        branchHasFavorite = true;
      }
    }

    return branchHasFavorite;
  };

  walk(props.inputItems, false);
  return { visibleKeys, autoExpandKeys };
});

// Важно: favoriteTreeState должен быть объявлен выше этого watcher с immediate,
// иначе при старте возможна TDZ-ошибка (Cannot access before initialization).
watch(() => props.favoriteMode, (enabled) => {
  // Авторазворачивание по избранному применяем только в момент включения режима.
  if (enabled) {
    if (!modeSessions.favorite.active) {
      startModeSession('favorite');
    }
    applyFavoriteAutoExpandOnce(props.inputItems, favoriteTreeState.value.autoExpandKeys);
    return;
  }
  if (modeSessions.favorite.active) {
    stopModeSession('favorite');
  }
}, { immediate: true });

function countChildren(el: any): number {
  let n = 0;
  for (const key of CHILD_ARRAYS) {
    const arr = el[key];
    if (arr) n += arr.length;
  }
  return n;
}

function getOrderedChildrenForRender(parent: any): BaseListItem[] {
  // Порядок детей берём строго из массивов данных (CHILD_ARRAYS) — они и есть источник истины.
  // Не пересортировываем по типу на рендере: серверный FolderChildren уже отсортирован, а
  // локально созданные/перемещённые элементы должны оставаться там, куда их положили мутации стора
  // (созданный — по центру вьюпорта, перемещённый — на свою отсортированную позицию).
  const result: BaseListItem[] = [];
  for (const arrKey of CHILD_ARRAYS) {
    const arr = (parent as any)[arrKey] as BaseListItem[] | undefined;
    if (!arr || arr.length === 0) continue;
    for (const child of arr) result.push(child);
  }
  return result;
}

/** isRoot здесь — только «буквально корневой» (depth === 0, без родителя). «Корневая выборка по смыслу» (в т.ч. выборка в папке) не меняется, считается в allowRootToRootDropAction. */
function flattenTree(
  items: BaseListItem[],
  depth: number,
  isRoot: boolean,
  parentId: number | undefined,
  parentType: BaseItemTypes | undefined,
  searchActive: boolean,
  favoriteActive: boolean,
  inReportContext: boolean,
): FlatItem[] {
  const result: FlatItem[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    const itemKey = makeSelectionKey(item);
    if (favoriteActive && !favoriteTreeState.value.visibleKeys.has(itemKey)) continue;
    if (searchActive && toRaw(item)._searchVisible === false) continue;

    const childCount = countChildren(item);
    const hasChildren = childCount > 0;
    const searchAutoExpand = searchActive && (item as any)._searchAutoExpand === true;
    const shouldExpand = searchActive
      ? (item.isExpanded || searchAutoExpand)
      : item.isExpanded;

    // Узел считается «в отчётном контексте», если он сам REPORT (корень)
    // или если унаследован inReportContext от родителя (любой потомок отчёта).
    const isReportRoot = item.elType === BaseItemTypes.REPORT;
    const itemInReportContext = inReportContext || isReportRoot;

    result.push({
      item,
      depth,
      isRoot,
      levelSiblings: items,
      siblingIndex: i,
      parentId,
      parentType,
      hasChildren,
      effectiveExpanded: !!shouldExpand && hasChildren,
      inReportContext: itemInReportContext,
      key: `${item.elType}_${item.Id ?? 0}_${item.FolderId ?? 0}_${item.SrcType ?? ''}_${item.DestType ?? ''}_d${depth}_i${i}`,
    });

    // В режиме поиска разворачиваем элемент, если он был раскрыт пользователем
    // ИЛИ если у него есть совпадения в поддереве (_searchAutoExpand, устанавливается в applyAnalyticsSearchFilter).
    // В режиме избранного авто-раскрытие применяется только один раз при включении режима
    // (через applyFavoriteAutoExpandOnce), далее учитывается только item.isExpanded.
    if (hasChildren && shouldExpand) {
      const orderedChildren = getOrderedChildrenForRender(item);
      const parentIdVal = item.elType === BaseItemTypes.FOLDER ? item.FolderId : item.Id;
      const sub = flattenTree(orderedChildren, depth + 1, false, parentIdVal, item.elType, searchActive, favoriteActive, itemInReportContext);
      for (let j = 0; j < sub.length; j++) result.push(sub[j]);
    }
  }
  return result;
}

// Используется в шаблоне для показа плейсхолдера «Нет совпадений» при пустом результате поиска.
// Локальная одноимённая переменная внутри flatItems оставлена как есть, чтобы не менять её область видимости.
const searchActive = computed(() => !!props.searchString);

const flatItems = computed<FlatItem[]>(() => {
  searchVersionRef.value; // reactive trigger for search flag changes
  const searchActive = !!props.searchString;
  const favoriteActive = !!props.favoriteMode;
  const result = flattenTree(props.inputItems, 0, true, undefined, undefined, searchActive, favoriteActive, false);
  return result;
});

/* Ключ строки для замеров тот же, что и :key в шаблоне. Без него виртуализатор хранит
   замеренные высоты по индексу строки, и при вставке строк (разворот узла) всё, что ниже,
   получает высоту чужой строки: наблюдатель размеров молчит, потому что сами узлы не
   менялись, они лишь сдвинулись по индексам, а лечилось это только уходом строки из окна
   и обратно. С ключом высота привязана к элементу дерева. В поиске набор видимых строк
   меняется, и ключ у элемента может стать другим (в него входят глубина и позиция среди
   видимых соседей) - тогда строка перемонтируется и замерится заново, чужую высоту
   получить всё равно нельзя. */
const rowVirtualizer = useVirtualizer(computed(() => ({
  count: flatItems.value.length,
  getScrollElement: () => scrollEl.value,
  estimateSize: () => 28,
  overscan: 10,
  getItemKey: (index: number) => flatItems.value[index]?.key ?? index,
})));

const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems());
const totalSize = computed(() => rowVirtualizer.value.getTotalSize());

// После резкой смены сегмента virtualizer может кратко вернуть старые индексы.
// Отсекаем их, чтобы шаблон не читал flatItems[index] как undefined.
const safeVirtualRows = computed(() => {
  return virtualRows.value
    .map((vRow: any) => ({ vRow, flat: flatItems.value[vRow.index] }))
    .filter((row: any) => !!row.flat);
});

/* Вертикальная направляющая уровня вложенности идёт по центру шеврона родителя.
   Отступ строки это depth * 22, сам шеврон шириной 16 с отступом слева 8, плюс 1px
   прозрачной рамки строки. В режиме выделения перед шевроном стоит чекбокс (16 + 8),
   но в дереве отчёта его нет. */
function guideLineLeft(level: number, inReportContext: boolean): number {
  const checkboxShift = selectionMode.value && !inReportContext ? 24 : 0;
  return level * 22 + 17 + checkboxShift;
}

function isSameSelectedIdentity(item: BaseListItem, selected: BaseListItem): boolean {
  if (!item || !selected) return false;
  if (selected.elType === BaseItemTypes.FOLDER) {
    return item.elType === BaseItemTypes.FOLDER &&
      (item as any).FolderId === (selected as any).FolderId;
  }
  return item.elType === selected.elType &&
    item.Id === selected.Id &&
    ((selected.SrcType != null) ? item.SrcType === selected.SrcType : !item.SrcType) &&
    item.DestType === selected.DestType &&
    ((!selected.ElementId && !item.ElementId) || item.ElementId === selected.ElementId);
}

function scrollToSelectedRow(retry: number = 0) {
  const selected = genstore.selectedItem as BaseListItem;
  if (!selected) return;
  const hasSelectableIdentity =
    (selected.elType === BaseItemTypes.FOLDER && !!selected.FolderId)
    || !!selected.Id
    || !!selected.ElementId;
  if (!hasSelectableIdentity) return;

  const index = flatItems.value.findIndex((fi) => isSameSelectedIdentity(fi.item, selected));
  if (index < 0) {
    if (retry < 8) {
      setTimeout(() => scrollToSelectedRow(retry + 1), 30);
    }
    return;
  }

  // Скроллим после раскрытия веток и пересчёта virtual rows.
  nextTick(() => {
    (rowVirtualizer.value as any).scrollToIndex(index, { align: 'center' });
  });
}

watch(
  () => genstore.triggerScrollToSelection,
  (trigger) => {
    if (!trigger) return;
    scrollToSelectedRow(0);
  },
);

let _measureQueue: HTMLElement[] = [];
let _measureRaf: number | null = null;
function drainMeasureQueue() {
  _measureRaf = null;
  const chunk = _measureQueue.splice(0, MEASURE_BATCH);
  for (const e of chunk) {
    try { rowVirtualizer.value.measureElement(e); } catch { /* swallow */ }
  }
  if (_measureQueue.length > 0) {
    _measureRaf = requestAnimationFrame(drainMeasureQueue);
  }
}

function measureElement(el: any) {
  if (!el) return;
  if (!_measureQueue.includes(el)) _measureQueue.push(el);
  if (!_measureRaf) {
    _measureRaf = requestAnimationFrame(drainMeasureQueue);
  }
}

function onToggleExpand(el: BaseListItem) {
  const anyEl: any = el;
  const autoSearch = anyEl._searchAutoExpand === true;
  markExpandDirtyByUser(el);

  // Если элемент был раскрыт только из-за автопоиска (isExpanded === false, _searchAutoExpand === true),
  // то первый клик должен просто свернуть его: отключаем авторазворачивание и НЕ включаем isExpanded.
  if (!el.isExpanded && autoSearch) {
    if (autoSearch) {
      anyEl._searchAutoExpand = false;
    }
    return;
  }

  // В остальных случаях работаем как обычный toggle.
  el.isExpanded = !el.isExpanded;
  anyEl._searchAutoExpand = false;
}

// --- Зоны «в корень»: полосы слева и справа (по 16px), подсветка пунктиром при наведении ---
function canDropToRootMulti(dragEl: any): boolean {
  if (selectionMode.value && selectedItems.size > 0) {
    for (const sel of selectedItems.values()) {
      if (!canDropToRoot(sel)) return false;
    }
    return true;
  }
  return canDropToRoot(dragEl);
}

function canDropToRootForDrag(dragEl: any): boolean {
  return canDropToRootMulti(dragEl);
}

function onRootDrop(item: DragItem, dropPosition?: { x: number; y: number }) {
  const dragEl = item.callbackParams ?? item;
  if (!canDropToRootMulti(dragEl)) return;

  onChangeParent({
    item_id: item.id,
    new_parent: null,
    new_pos: 0,
    dragParams: item.callbackParams,
    hoverParams: {},
    moveTargetKind: 'root',
    dropPosition: dropPosition ?? { ...lastClickPos.value },
  });
}

function onChangeParent(params: any) {
  emit('changeParent', params);
}
const [rootDropLeftCollected, rootDropLeftRef] = useDrop<DragItem, void, { isOver: boolean }>({
  accept: [DragItemTypes.CARD_ROOT, DragItemTypes.CARD_CHILD],
  drop(item: DragItem, monitor) {
    const dragEl = item.callbackParams ?? item;
    if (!canDropToRootMulti(dragEl)) return;
    const dropOffset = monitor.getClientOffset();
    onRootDrop(item, dropOffset ? { x: dropOffset.x, y: dropOffset.y } : undefined);
  },
  collect: (monitor) => {
    const item = monitor.getItem() as DragItem | null;
    const dragEl = item?.callbackParams ?? item;
    const allowed = !!dragEl && canDropToRootMulti(dragEl);
    return { isOver: allowed && monitor.isOver() };
  },
});
const [rootDropRightCollected, rootDropRightRef] = useDrop<DragItem, void, { isOver: boolean }>({
  accept: [DragItemTypes.CARD_ROOT, DragItemTypes.CARD_CHILD],
  drop(item: DragItem, monitor) {
    const dragEl = item.callbackParams ?? item;
    if (!canDropToRootMulti(dragEl)) return;
    const dropOffset = monitor.getClientOffset();
    onRootDrop(item, dropOffset ? { x: dropOffset.x, y: dropOffset.y } : undefined);
  },
  collect: (monitor) => {
    const item = monitor.getItem() as DragItem | null;
    const dragEl = item?.callbackParams ?? item;
    const allowed = !!dragEl && canDropToRootMulti(dragEl);
    return { isOver: allowed && monitor.isOver() };
  },
});
const rootDropLeftOver = computed(() => rootDropLeftCollected.value?.isOver ?? false);
const rootDropRightOver = computed(() => rootDropRightCollected.value?.isOver ?? false);

// --- Расчёт оптимальной позиции вставки нового элемента (по центру видимой области) ---

/**
 * Сравнение identity элемента из flatItem с переданным parentIdentity.
 * Для папок: elType === FOLDER && FolderId === FolderId
 * Для остальных: elType + Id + SrcType + DestType (строго, без упрощений)
 */
function matchIdentity(item: BaseListItem, identity: any): boolean {
  if (!item || !identity) return false;
  if (identity.elType === BaseItemTypes.FOLDER) {
    return item.elType === BaseItemTypes.FOLDER &&
      (item as any).FolderId === identity.FolderId;
  }
  return item.elType === identity.elType &&
    item.Id === identity.Id &&
    ((identity.SrcType != null) ? (item as any).SrcType === identity.SrcType : !(item as any).SrcType) &&
    (item as any).DestType === identity.DestType;
}

/**
 * Синхронно рассчитывает оптимальную позицию вставки нового элемента по центру видимой области.
 *
 * Функция СИНХРОННАЯ — вызывается ДО async API-запроса на создание, пока вьюпорт стабилен.
 * Это исключает гонку: позиция фиксируется до того, как данные начнут меняться.
 *
 * Алгоритм:
 * 1. Находит всех siblings на целевом depth (без фильтрации по типу — учитываются графики,
 *    выборки, папки и т.д. вперемешку, как они идут визуально).
 * 2. Для каждого sibling считает середину его ПОЛНОГО блока (строка + все раскрытые потомки),
 *    а не только строку-заголовок. Так раскрытый сосед с потомками учитывается целиком.
 * 3. Находит промежуток между серединами блоков, ближайший к центру вьюпорта.
 * 4. Возвращает глобальную позицию (0-based среди ВСЕХ потомков). Пересчёт в позицию
 *    внутри конкретного массива (Charts, FolderChildren и т.д.) делает insertElementLocally.
 *
 * @param parentIdentity — identity родителя (null = корень). Строго по полной identity.
 * @param newElType — тип создаваемого элемента (не используется для расчёта, но передаётся для единообразия API)
 * @returns глобальная позиция вставки
 */
function calcOptimalInsertPosition(parentIdentity: any | null, newElType: BaseItemTypes): number {
  const items = flatItems.value;
  if (items.length === 0) return 0;

  const el = scrollEl.value;
  if (!el) return 0;

  const viewportCenter = el.scrollTop + el.clientHeight / 2;

  let targetDepth: number;
  let siblingStartIdx: number;
  let siblingEndIdx: number;

  if (parentIdentity === null) {
    targetDepth = 0;
    siblingStartIdx = 0;
    siblingEndIdx = items.length;
  } else {
    let parentFlatIdx = -1;
    for (let i = 0; i < items.length; i++) {
      if (matchIdentity(items[i].item, parentIdentity)) {
        parentFlatIdx = i;
        break;
      }
    }
    if (parentFlatIdx < 0) return 0;

    targetDepth = items[parentFlatIdx].depth + 1;
    siblingStartIdx = parentFlatIdx + 1;
    siblingEndIdx = items.length;
    for (let i = siblingStartIdx; i < items.length; i++) {
      if (items[i].depth <= items[parentFlatIdx].depth) {
        siblingEndIdx = i;
        break;
      }
    }
  }

  // Собрать ВСЕ siblings на targetDepth (без фильтрации по типу)
  interface SiblingInfo { flatIdx: number; globalOrder: number; }
  const siblings: SiblingInfo[] = [];
  let globalOrder = 0;
  for (let i = siblingStartIdx; i < siblingEndIdx; i++) {
    if (items[i].depth !== targetDepth) continue;
    siblings.push({ flatIdx: i, globalOrder });
    globalOrder++;
  }

  if (siblings.length === 0) return 0;

  // Y-позиции из виртуализатора: getVirtualItems() возвращает отрендеренные строки (видимые + overscan).
  // Берём ВЕРХ строки (start). Для строк вне виртуального окна — грубая оценка flatIdx * 28 (estimateSize).
  const vRows = rowVirtualizer.value.getVirtualItems();
  const topMap = new Map<number, number>();
  for (const vr of vRows) {
    topMap.set(vr.index, vr.start);
  }
  const topY = (idx: number): number => {
    const t = topMap.get(idx);
    return t !== undefined ? t : idx * 28;
  };

  // Для каждого соседа берём середину его ПОЛНОГО блока (строка соседа + все его раскрытые потомки),
  // а не только строку-заголовок. В плоском списке потомки соседа s_i лежат строками между s_i и
  // следующим соседом того же уровня, поэтому верх следующего соседа = низ блока s_i (для последнего —
  // граница поддерева siblingEndIdx). Свёрнут → потомков нет в flatItems → блок = одна строка;
  // раскрыт → блок включает потомков, и его середина смещается вниз.
  const siblingYs: number[] = siblings.map((s, k) => {
    const blockTop = topY(s.flatIdx);
    const blockBottomIdx = (k + 1 < siblings.length) ? siblings[k + 1].flatIdx : siblingEndIdx;
    const blockBottom = topY(blockBottomIdx);
    return (blockTop + blockBottom) / 2;
  });

  // Ищем промежуток, ближайший к центру вьюпорта.
  // Для N siblings есть N+1 позиций вставки: 0=перед первым, i=между [i-1] и [i], N=после последнего.
  // Крайние случаи: если центр выше всех siblings → 0 (в начало), ниже всех → N (в конец).
  if (viewportCenter <= siblingYs[0]) return 0;
  if (viewportCenter >= siblingYs[siblingYs.length - 1]) return siblings.length;

  for (let i = 0; i < siblingYs.length - 1; i++) {
    if (viewportCenter >= siblingYs[i] && viewportCenter <= siblingYs[i + 1]) {
      return i + 1;
    }
  }

  return Math.floor(siblings.length / 2);
}

__expose({
  calcOptimalInsertPosition,
  selectionMode,
  enterSelectionMode,
  exitSelectionMode,
  getSelectedIdentities,
  getEffectiveSelectedIdentities,
  selectedItems,
  flatItems,
  effectiveSelectedCount,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (selectionMode.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("span", {
              ref_key: "deleteBtnRef",
              ref: deleteBtnRef,
              class: "selection-panel__btn",
              onClick: deleteSelected
            }, "Удалить", 512),
            _createElementVNode("span", {
              class: "selection-panel__btn",
              onClick: resetSelection
            }, "Сбросить")
          ]),
          _createElementVNode("span", {
            class: "selection-panel__btn",
            onClick: exitSelectionMode
          }, "Отмена")
        ]))
      : _createCommentVNode("", true),
    (!selectionMode.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 1,
          type: "button",
          class: _normalizeClass(["tree-back", { 'tree-back--disabled': !_ctx.backEnabled }]),
          disabled: !_ctx.backEnabled,
          onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('back')))
        }, [
          _cache[16] || (_cache[16] = _createElementVNode("svg", {
            class: "tree-back__chevron",
            width: "16",
            height: "16",
            viewBox: "0 0 16 16",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg"
          }, [
            _createElementVNode("path", {
              d: "M4 6L8 10L12 6",
              stroke: "#D2D2D2",
              "stroke-width": "2",
              "stroke-linecap": "round",
              "stroke-linejoin": "round"
            })
          ], -1)),
          _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t('go_back')), 1)
        ], 10, _hoisted_4))
      : _createCommentVNode("", true),
    (searchActive.value && flatItems.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_ctx.$t('no_matches')), 1))
      : (_openBlock(), _createElementBlock("div", _hoisted_7, [
          (!betweenRootDropViaIndicator.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                ref: _unref(rootDropLeftRef),
                class: _normalizeClass(["root-drop-strip root-drop-strip--left", { 'root-drop-strip--over': rootDropLeftOver.value }])
              }, null, 2))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            ref_key: "scrollEl",
            ref: scrollEl,
            class: _normalizeClass(["virtual-tree-scroll", {
          'virtual-tree-scroll--compact': selectionMode.value,
          'virtual-tree-scroll--with-back': !selectionMode.value,
        }])
          }, [
            _createElementVNode("div", {
              class: "virtual-tree-scroll-inner",
              style: _normalizeStyle({ minHeight: totalSize.value + 'px' })
            }, [
              _createElementVNode("div", {
                class: "virtual-tree-scroll-content",
                style: _normalizeStyle({ height: totalSize.value + 'px' })
              }, [
                _createElementVNode("div", {
                  style: _normalizeStyle({ height: totalSize.value + 'px', width: '100%', position: 'relative' })
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(safeVirtualRows.value, (row) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: row.flat.key,
                      class: _normalizeClass({ 'vrow-animate': _unref(insertAnimActive) }),
                      "data-index": row.vRow.index,
                      ref_for: true,
                      ref: measureElement,
                      style: _normalizeStyle({
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${row.vRow.start}px)`,
              })
                    }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.flat.depth, (level) => {
                        return (_openBlock(), _createElementBlock("span", {
                          key: 'guide-' + level,
                          class: "vtree-guide",
                          style: _normalizeStyle({ left: guideLineLeft(level - 1, row.flat.inReportContext) + 'px' })
                        }, null, 4))
                      }), 128)),
                      _createVNode(BaseListElementLite, {
                        element: row.flat.item,
                        depth: row.flat.depth,
                        "is-root": row.flat.isRoot,
                        "rename-siblings": row.flat.levelSiblings,
                        "has-children": row.flat.hasChildren,
                        "effective-expanded": row.flat.effectiveExpanded,
                        "input-index": row.flat.siblingIndex,
                        "parent-id": row.flat.parentId,
                        "parent-type": row.flat.parentType,
                        "in-report-context": row.flat.inReportContext,
                        "into-forbidden": intoForbiddenFn,
                        "scroll-viewport": scrollEl.value,
                        "allow-highlight-on-hover": allowHighlightOnHover,
                        "allow-hover-action": allowRootToRootDropAction,
                        "allow-child-to-child-drop-action": allowChildToChildDropAction,
                        "allow-child-to-root-drop-action": allowChildToRootDropAction,
                        "allow-root-to-child-drop-action": allowRootToChildDropAction,
                        "selection-mode": selectionMode.value,
                        "is-checked": isEffectivelySelected(row.flat.item),
                        "is-inherited": isInheritedSelected(row.flat.item),
                        "is-create-folder-pending-for-this-parent": !!_ctx.pendingCreateFolderByParent?.[row.flat.item.FolderId],
                        "is-move-pending-for-this-item": !!_ctx.pendingMoveByKey?.[makeSelectionKey(row.flat.item)],
                        "between-root-drop-via-indicator": betweenRootDropViaIndicator.value,
                        "can-drop-to-root-for-drag": canDropToRootForDrag,
                        onToggleExpand: onToggleExpand,
                        onChangeParent: onChangeParent,
                        onCardSelected: _cache[1] || (_cache[1] = (arg) => emit('cardSelected', arg)),
                        onCardDeleted: _cache[2] || (_cache[2] = (el, pos) => emit('cardDeleted', el, pos)),
                        onCardFavorite: _cache[3] || (_cache[3] = (arg) => emit('cardFavorite', arg)),
                        onCardCloned: _cache[4] || (_cache[4] = (arg) => emit('cardCloned', arg)),
                        onCardRenamed: _cache[5] || (_cache[5] = (arg) => emit('cardRenamed', arg)),
                        onAddChildSelection: _cache[6] || (_cache[6] = (arg) => emit('addChildSelection', arg)),
                        onAddChildProjection: _cache[7] || (_cache[7] = (arg) => emit('addChildProjection', arg)),
                        onAddChildList: _cache[8] || (_cache[8] = (arg) => emit('addChildList', arg)),
                        onAddChildChart: _cache[9] || (_cache[9] = (arg) => emit('addChildChart', arg)),
                        onAddChildFolder: _cache[10] || (_cache[10] = (arg) => emit('addChildFolder', arg)),
                        onAddToReport: _cache[11] || (_cache[11] = (arg) => emit('addToReport', arg)),
                        onEnterSelectionMode: enterSelectionMode,
                        onToggleItemSelection: onToggleItemSelection,
                        onSwitchProjection: _cache[12] || (_cache[12] = (arg) => emit('cardSelected', { element: arg.element, switch: true, segment: arg.isRoot ? arg.element.SrcType : arg.element.DestType, scrollto: true })),
                        onBreakAfter: _cache[13] || (_cache[13] = (arg) => emit('breakAfter', arg)),
                        onChangePosition: _cache[14] || (_cache[14] = (arg) => emit('changePosition', arg)),
                        onCardMoved: _cache[15] || (_cache[15] = (arg) => emit('cardMoved', arg))
                      }, null, 8, ["element", "depth", "is-root", "rename-siblings", "has-children", "effective-expanded", "input-index", "parent-id", "parent-type", "in-report-context", "scroll-viewport", "selection-mode", "is-checked", "is-inherited", "is-create-folder-pending-for-this-parent", "is-move-pending-for-this-item", "between-root-drop-via-indicator"])
                    ], 14, _hoisted_8))
                  }), 128))
                ], 4)
              ], 4),
              (!betweenRootDropViaIndicator.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    ref: _unref(rootDropRightRef),
                    class: _normalizeClass(["root-drop-strip root-drop-strip--right", { 'root-drop-strip--over': rootDropRightOver.value }])
                  }, null, 2))
                : _createCommentVNode("", true)
            ], 4)
          ], 2)
        ])),
    (confirmPopup.show)
      ? (_openBlock(), _createBlock(ConfirmActionPopup, {
          key: 4,
          title: confirmPopup.title,
          question: confirmPopup.question,
          "confirm-label": confirmPopup.confirmLabel,
          "cancel-label": confirmPopup.cancelLabel,
          position: confirmPopup.position,
          onConfirm: confirmPopup.onConfirm,
          onCancel: confirmPopup.onCancel
        }, null, 8, ["title", "question", "confirm-label", "cancel-label", "position", "onConfirm", "onCancel"]))
      : _createCommentVNode("", true)
  ]))
}
}

})